import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {boolean} */
  #opening = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller per connect, so a card that is morphed out and back in
    // neither leaks its old document listeners nor reconnects without any. The
    // variantSelected listener used to be added and removed via separate
    // .bind(this) calls, which produce different functions - so it was never
    // actually removed and every re-insert stacked another one.
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    // dynamic-bundle products load a scent picker (bundle-picker-component), not a
    // variant-picker, so there's nothing to re-render here. Bail before the null
    // deref would throw and abort handleClick before #openQuickAddModal() runs —
    // which is what stopped the Quick Add "Choose" modal from opening for the
    // Custom N-Pack bundles (so their _components never synced and the cart line
    // never expanded). See the dynamic-bundle path in handleClick.
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Opening fetches the whole PDP document before anything appears on screen,
    // which reads as a dead button. Ignore repeat clicks while that is in flight:
    // a second click aborted the first fetch, which resolved to null content and
    // opened the modal empty (or showing the previously opened product).
    if (this.#opening) return;
    this.#opening = true;
    this.#setBusy(true);

    try {
      await this.#open();
    } catch (error) {
      // Previously this rejected out of an un-awaited handler, so the modal was
      // never opened and the click did nothing at all, with no feedback.
      console.error('[quick-add] failed to open the quick add modal', error);
    } finally {
      this.#opening = false;
      this.#setBusy(false);
    }
  };

  /**
   * Marks the card busy while the PDP fetch is in flight. Scoped to the Choose
   * button, which is the only one that triggers this path - the Add button's
   * disabled state tracks variant availability and isn't ours to touch.
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.toggleAttribute('data-quick-add-busy', busy);

    for (const button of this.querySelectorAll('.quick-add__button--choose')) {
      if (button instanceof HTMLButtonElement) button.disabled = busy;
    }
  }

  /**
   * Loads the product content and opens the quick add modal.
   */
  async #open() {
    // custom-bundle products load a compact instance of the bundle builder into
    // the modal instead of the standard product grid, so the shopper builds a
    // correctly-formed (_rc_bundle) bundle without leaving the page.
    if (this.dataset.bundleBuilder === 'true') {
      await this.#openBundleBuilderModal();
      return;
    }

    // dynamic-bundle products use the standard grid modal flow (the PDP's
    // product-grid-content already contains the scent picker + add-to-cart form),
    // but the picker's custom element isn't loaded on card pages. Register it so
    // the morphed picker upgrades and re-syncs the bundle _components into the
    // form. On failure the card's "Build set" PDP link remains a working fallback.
    if (
      this.dataset.bundlePicker === 'true' &&
      this.dataset.bundlePickerSrc &&
      !customElements.get('bundle-picker-component')
    ) {
      try {
        await import(this.dataset.bundlePickerSrc);
      } catch (error) {
        // Without the picker the modal renders dead dropdowns over a form that
        // carries no _components, so send the shopper to the PDP instead - the
        // same destination the card's "Build set" link points at. (The link's own
        // href can't serve as the fallback here: this handler preventDefault()s.)
        console.error('[quick-add] failed to load bundle picker module', error);
        if (this.productPageUrl) window.location.assign(this.productPageUrl);
        return;
      }
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    // Nothing loaded (the fetch failed, or returned a page with no grid content).
    // Opening now would show an empty modal - or worse, the previously opened
    // product's content - so leave the page as it is.
    if (!productGrid) {
      console.error('[quick-add] no product content loaded; not opening the modal');
      return;
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal();
  }

  /**
   * Opens the Quick Add modal with a compact, reused instance of the
   * subscription-bundle-builder for custom-bundle products. The builder
   * self-initializes on insertion and, via data-context="quick-add", opens
   * pre-filled with a default set (see subscription-bundle-builder.js). It adds
   * to cart with the correct _rc_bundle line-item properties and dispatches a
   * bubbling CartUpdateEvent that closes this dialog.
   */
  async #openBundleBuilderModal() {
    const currentUrl = this.productPageUrl;

    // The builder's module isn't loaded on card pages (collection/search/home).
    // Lazy-load it once; the customElements guard + browser module cache make
    // repeat clicks and multiple cards a no-op. On failure, bail — the card's
    // buy-buttons "Build bundle" PDP link remains a working fallback.
    if (!customElements.get('subscription-bundle-builder') && this.dataset.bundleBuilderSrc) {
      try {
        await import(this.dataset.bundleBuilderSrc);
      } catch (error) {
        console.error('[quick-add] failed to load bundle builder module', error);
        return;
      }
    }

    // Cache the parsed (never-upgraded) builder markup per URL; clone fresh per open.
    let builder = this.#cachedContent.get(currentUrl);

    if (!builder) {
      const html = await this.fetchProductPage(currentUrl);
      const element = html?.querySelector('subscription-bundle-builder');
      if (element) {
        builder = /** @type {Element} */ (element.cloneNode(true));
        this.#cachedContent.set(currentUrl, builder);
      }
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    if (builder && modalContent) {
      const freshBuilder = /** @type {Element} */ (builder.cloneNode(true));
      // Set the modal context BEFORE insertion so connectedCallback runs in
      // modal mode (auto-fill default set, skip the PDP draft).
      freshBuilder.setAttribute('data-context', 'quick-add');
      modalContent.replaceChildren(freshBuilder);
    }

    this.#openQuickAddModal();
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      // dynamic-bundle scent picker (lives in .product-details, which is removed
      // below) — keep it so shoppers can edit the bundle in the mobile modal.
      const bundlePicker = productGrid.querySelector('bundle-picker-component');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (bundlePicker) {
        productGrid.appendChild(bundlePicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // Forward the originating card's surface to the modal's product-form-component so
    // GA4 attribution survives the morph. GA4-only — product card adds don't carry
    // properties[_source] on the Shopify side (those are reserved for upsell surfaces).
    const modalProductForm = modalContent.querySelector('product-form-component');
    if (modalProductForm) {
      modalProductForm.dataset.gaListName = this.dataset.source || 'Product Card';
    }

    // The picker sits ahead of the product form in the grid, and on a re-open morph
    // reuses the element already in the modal (so connectedCallback doesn't run).
    // Either way its own sync can't have seen the form - re-sync now that the whole
    // subtree is in the DOM, so the add carries `properties[_components]`.
    const bundlePicker = /** @type {any} */ (modalContent.querySelector('bundle-picker-component'));
    if (typeof bundlePicker?.syncNow === 'function') bundlePicker.syncNow();

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    // Bundle products must go through their builder/picker to pick up the line
    // item properties the cart expands, so they stay on "Choose" regardless of
    // how many options the parent product has. Swapping them to a direct "Add"
    // would submit the bare parent variant and produce an unexpandable order.
    if (this.dataset.bundlePicker === 'true' || this.dataset.bundleBuilder === 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
